import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Package, MapPin, Calendar, Search, Filter, Ship, Plane, Truck, Eye } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';

const RFQList = () => {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [myRfqs, setMyRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchRFQs();
  }, []);

  const fetchRFQs = async () => {
    try {
      const [allResponse, myResponse] = await Promise.all([
        api.get('/rfqs'),
        api.get('/rfqs/my')
      ]);
      setRfqs(allResponse.data);
      setMyRfqs(myResponse.data);
    } catch (error) {
      toast.error('Failed to load RFQs');
    } finally {
      setLoading(false);
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air': return <Plane className="h-4 w-4" />;
      case 'sea': return <Ship className="h-4 w-4" />;
      case 'land': return <Truck className="h-4 w-4" />;
      default: return <Package className="h-4 w-4" />;
    }
  };

  const baseRfqs = activeTab === 'all' ? rfqs : myRfqs;
  
  const displayRfqs = baseRfqs.filter(rfq => {
    const matchesSearch = rfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfq.origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfq.destination.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMode = filterMode === 'all' || rfq.mode === filterMode;
    const matchesStatus = filterStatus === 'all' || rfq.status === filterStatus;
    return matchesSearch && matchesMode && matchesStatus;
  });

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-bold text-ocean-900">RFQ Marketplace</h1>
              <p className="text-sm sm:text-base text-slate-600 mt-1">Find and respond to freight opportunities</p>
            </div>
            <Button
              onClick={() => navigate('/rfqs/create')}
              className="bg-cyan-500 hover:bg-cyan-600 w-full sm:w-auto text-sm sm:text-base"
              data-testid="create-rfq-button"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create RFQ
            </Button>
          </div>

          {/* Search and Filters */}
          <Card className="mb-4 sm:mb-6">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by title, origin, or destination..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 text-sm sm:text-base"
                    data-testid="rfq-search-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <Select value={filterMode} onValueChange={setFilterMode}>
                    <SelectTrigger className="text-sm" data-testid="mode-filter">
                      <SelectValue placeholder="Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modes</SelectItem>
                      <SelectItem value="air">Air</SelectItem>
                      <SelectItem value="sea">Sea</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="multimodal">Multimodal</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="text-sm" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="awarded">Awarded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto">
            <Button
              variant={activeTab === 'all' ? 'default' : 'outline'}
              onClick={() => setActiveTab('all')}
              className={`flex-shrink-0 text-xs sm:text-sm ${activeTab === 'all' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}`}
              data-testid="all-rfqs-tab"
            >
              Marketplace ({rfqs.length})
            </Button>
            <Button
              variant={activeTab === 'my' ? 'default' : 'outline'}
              onClick={() => setActiveTab('my')}
              className={`flex-shrink-0 text-xs sm:text-sm ${activeTab === 'my' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}`}
              data-testid="my-rfqs-tab"
            >
              My RFQs ({myRfqs.length})
            </Button>
          </div>

          {/* Results count */}
          <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4">
            Showing {displayRfqs.length} of {baseRfqs.length} RFQs
          </p>

          {/* RFQ List */}
          {displayRfqs.length === 0 ? (
            <Card>
              <CardContent className="p-8 sm:p-12 text-center text-slate-600">
                <Package className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-slate-400" />
                <p className="mb-2 text-sm sm:text-base">No RFQs found</p>
                <p className="text-xs sm:text-sm mb-3 sm:mb-4">
                  {searchTerm || filterMode !== 'all' || filterStatus !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Be the first to create an RFQ'}
                </p>
                {activeTab === 'my' && (
                  <Button
                    onClick={() => navigate('/rfqs/create')}
                    className="bg-cyan-500 hover:bg-cyan-600 text-sm"
                  >
                    Create Your First RFQ
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {displayRfqs.map((rfq) => (
                <Card
                  key={rfq.id}
                  className="hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 border-l-transparent hover:border-l-cyan-500"
                  onClick={() => navigate(`/rfqs/${rfq.id}`)}
                  data-testid={`rfq-card-${rfq.id}`}
                >
                  <CardHeader className="p-3 sm:p-4 lg:p-6 pb-2 sm:pb-3">
                    <div className="flex justify-between items-start mb-2">
                      <Badge className={`text-[10px] sm:text-xs ${
                        rfq.status === 'open' ? 'bg-green-100 text-green-700' :
                        rfq.status === 'closed' ? 'bg-slate-100 text-slate-700' :
                        rfq.status === 'awarded' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {rfq.status.toUpperCase()}
                      </Badge>
                      <div className="flex items-center gap-1 text-slate-500">
                        {getModeIcon(rfq.mode)}
                        <span className="text-[10px] sm:text-xs capitalize">{rfq.mode}</span>
                      </div>
                    </div>
                    <CardTitle className="text-sm sm:text-base lg:text-lg line-clamp-2">{rfq.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 lg:p-6 pt-0 sm:pt-0">
                    <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                      <div className="flex items-center gap-1.5 sm:gap-2 text-slate-700 font-medium bg-slate-50 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg">
                        <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-500 flex-shrink-0" />
                        <span className="truncate">{rfq.origin}</span>
                        <span className="text-slate-400 flex-shrink-0">→</span>
                        <span className="truncate">{rfq.destination}</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-600">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Package className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="capitalize text-xs sm:text-sm">{rfq.cargo_type?.replace('_', ' ')}</span>
                        </div>
                        {rfq.weight && (
                          <span className="text-[10px] sm:text-xs">{rfq.weight} {rfq.weight_unit}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2 text-slate-500 text-[10px] sm:text-xs">
                        <Calendar className="h-3 w-3" />
                        <span>Posted {new Date(rfq.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 sm:pt-3 border-t">
                        <span className="text-[10px] sm:text-xs text-slate-500 truncate max-w-[80px] sm:max-w-[120px]" title={rfq.company_name}>
                          {rfq.company_name}
                        </span>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-500">
                            <Eye className="h-3 w-3" />
                            {rfq.views_count}
                          </span>
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">
                            {rfq.quotes_count} quotes
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RFQList;
